<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    header="Edit Event Tolerances"
    class="w-full lg:max-w-4xl"
  >
    <div class="flex flex-col gap-4">
      <div v-if="eventTypes.length === 0" class="text-center py-4">
        <div class="text-surface-500 dark:text-surface-400">Loading event types...</div>
      </div>

      <div v-else class="space-y-6">
        <!-- Master Tolerance Selector -->
        <div class="bg-surface-50 dark:bg-surface-950 p-4 rounded-lg border border-surface-200 dark:border-surface-700">
          <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
            <span class="font-semibold">Master Tolerance</span>
            <p class="text-xs text-surface-500 dark:text-surface-400 mt-1">Default tolerance for all event types. Individual event types can override this setting.</p>
          </label>
          <Select
            v-model="masterTolerance"
            :options="toleranceOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select master tolerance"
            class="w-full"
          >
            <template #value="slotProps">
              <div v-if="slotProps.value" class="flex items-center gap-2">
                <!-- circle with tolerance color -->
                <div class="size-4 rounded-full" :class="`border-2 ${ getToleranceColor(slotProps.value).backgroundColor + ' ' + getToleranceColor(slotProps.value).borderColor }`"></div>
                <div>{{ slotProps.value }}</div>
              </div>
              <span v-else>
                {{ slotProps.placeholder }}
              </span>
            </template>
            <template #option="slotProps">
              <div class="flex items-center gap-2">
                <div class="size-4 rounded-full" :class="`border-2 ${ getToleranceColor(slotProps.option.value).backgroundColor + ' ' + getToleranceColor(slotProps.option.value).borderColor }`"></div>
                <div>{{ slotProps.option.value }}</div>
              </div>
            </template>
          </Select>
        </div>

        <!-- Individual Event Type Tolerances -->
        <div class="space-y-4">
          <h3 class="text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">Individual Event Type Overrides</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            <div v-for="eventType in eventTypes" :key="eventType.EVENT_TYPE_ID" class="flex items-center gap-3">
              <div class="flex-1">
                <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                  <span>{{ formatUpperUnderscoreCase(eventType.EVENT_TYPE).replace("Excpected", "Expected") }}</span>
                  <p v-if="eventType.DESCRIPTION" class="text-xs text-surface-500 dark:text-surface-400">{{ eventType.DESCRIPTION }}</p>
                </label>
                <Select
                  v-model="eventTolerances[eventType.EVENT_TYPE]"
                  :options="toleranceOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Use master tolerance"
                  class="w-full"
                >
                <template #value="slotProps">
                    <div v-if="slotProps.value" class="flex items-center gap-2">
                        <!-- circle with tolerance color -->
                        <div class="size-4 rounded-full" :class="`border-2 ${ getToleranceColor(slotProps.value).backgroundColor + ' ' + getToleranceColor(slotProps.value).borderColor }`"></div>
                        <div>{{ slotProps.value }}</div>
                    </div>
                    <span v-else>
                        {{ slotProps.placeholder }}
                    </span>
                </template>
                <template #option="slotProps">
                    <div class="flex items-center gap-2">
                        <div class="size-4 rounded-full" :class="`border-2 ${ getToleranceColor(slotProps.option.value).backgroundColor + ' ' + getToleranceColor(slotProps.option.value).borderColor }`"></div>
                        <div>{{ slotProps.option.value }}</div>
                    </div>
                </template>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
    <template #footer>
      <div class="flex justify-end gap-2 mt-2">
        <Button label="Cancel" severity="secondary" @click="onCancel" />
        <Button label="Update All" :loading="submitting" :disabled="submitting" @click="onSubmit" />
      </div>

    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed, nextTick } from 'vue'
import Dialog from 'primevue/dialog'
import Select from 'primevue/select'
import Button from 'primevue/button'
import { useToast } from 'primevue/usetoast'
import { useFrontegg } from '@frontegg/vue'
import { useFCCMerchantStore } from '@/stores/fcc'
import { errorToastWithDetails } from '@/util/toasts'
import { normalizeError } from '@/util/errorNormalizer'
import { formatUpperUnderscoreCase } from '@/util/util'
import { getToleranceColor } from '@/util/fcc/FCCUtil'

const merchantStore = useFCCMerchantStore()
const { authState } = useFrontegg()
const toast = useToast()

// Use ref instead of computed getter/setter to avoid deprecation warning
const isVisible = ref(false)

// Watch store changes to update local ref
watch(() => merchantStore.isEditModalOpen, (newValue) => {
  isVisible.value = newValue
})

// Watch local ref changes to update store
watch(isVisible, (newValue) => {
  merchantStore.setIsEditModalOpen(newValue)
})

const toleranceOptions = [
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' },
]

const eventTypes = computed(() => merchantStore.eventTypes)
const eventTolerances = ref<Record<string, string>>({})
const masterTolerance = ref<string>('')
const submitting = ref(false)

watch(
  () => isVisible.value,
  async (open) => {
    if (open) {
      // Fetch event types first if not already loaded
      if (eventTypes.value.length === 0) {
        try {
          await merchantStore.fetchEventTypes(authState)
        } catch (error) {
          console.error('Failed to fetch event types:', error)
        }
      }

      // Initialize tolerances for all event types
      eventTolerances.value = {}
      eventTypes.value.forEach(eventType => {
        eventTolerances.value[eventType.EVENT_TYPE] = eventType.DEFAULT_TOLERANCE || 'Medium'
      })

      // Initialize master tolerance from current merchant
      if (merchantStore.currentMerchant) {
        masterTolerance.value = merchantStore.currentMerchant.TOLERANCE_ID || 'Medium'
      } else {
        masterTolerance.value = 'Medium'
      }

      // If we have a current merchant, try to fetch their current tolerances
      if (merchantStore.currentMerchantId) {
        try {
          await merchantStore.fetchMerchantEventTolerances(
            authState,
            merchantStore.currentMerchantId
          )
          // Update the form with current tolerances if available
          if (merchantStore.currentMerchantEventTolerances.length > 0) {
            // This would need to be mapped properly based on your API response structure
            // For now, we'll use the default Medium for all
          }
        } catch (error) {
          console.error('Failed to fetch current tolerances:', error)
        }
      }
    } else {
      eventTolerances.value = {}
      masterTolerance.value = ''
    }
  },
)

function onCancel() {
  isVisible.value = false
}

async function onSubmit() {
  if (!merchantStore.currentMerchantId) return

  const wasAlreadyProcessing = submitting.value
  submitting.value = true
  if (wasAlreadyProcessing) return

  try {

    // Update master tolerance first
    if (masterTolerance.value) {
      await merchantStore.updateMerchantTolerance(
        authState,
        merchantStore.currentMerchantId,
        masterTolerance.value
      )
    }

    // Update tolerance for each event type that has a specific value set
    const updatePromises = Object.entries(eventTolerances.value)
      .filter(([, tolerance]) => tolerance && tolerance.trim() !== '')
      .map(([eventType, tolerance]) =>
        merchantStore.updateMerchantEventToleranceValue(
          authState,
          merchantStore.currentMerchantId!,
          eventType,
          tolerance
        )
      )

    await Promise.all(updatePromises)
    await nextTick()
    isVisible.value = false
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Tolerance settings updated successfully',
      life: 3000,
    })
  } catch (error) {
    console.error('Failed to update event tolerances:', error)
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
  } finally {
    submitting.value = false
  }
}
</script>
